import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, Card, CardContent, CircularProgress } from '@mui/material';
import { PictureAsPdf, Description, InsertDriveFile } from '@mui/icons-material';

interface DocumentFile {
  id: string;
  signedUrl: string;
  name: string;
  size?: number;
  type?: string;
  lastModified?: Date;
}

interface Props {
  files: DocumentFile[];
  maxPreviewSize?: number;
}

const DocumentsPreview: React.FC<Props> = ({ files, maxPreviewSize = 200 }) => {
  const [previewData, setPreviewData] = useState<Record<string, string>>({});
  const [loadingPreviews, setLoadingPreviews] = useState<Record<string, boolean>>({});

  const getFileIcon = (fileName: string, type?: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const fileType = type?.toLowerCase();

    if (fileType?.includes('pdf') || extension === 'pdf') {
      return <PictureAsPdf sx={{ fontSize: 48, color: '#d32f2f' }} />;
    }
    if (['doc', 'docx'].includes(extension || '') || fileType?.includes('document')) {
      return <Description sx={{ fontSize: 48, color: '#1976d2' }} />;
    }
    return <InsertDriveFile sx={{ fontSize: 48, color: '#666' }} />;
  };

  const generatePdfPreview = async (file: DocumentFile) => {
    if (!file.type?.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      return;
    }

    setLoadingPreviews((prev) => ({ ...prev, [file.id]: true }));

    try {
      // Aquí podemos usar una librería como pdf.js para generar thumbnails
      // Por ahora, solo indicamos que es un PDF
      setPreviewData((prev) => ({ ...prev, [file.id]: 'pdf-preview' }));
    } catch (error) {
      console.error('Error generating PDF preview:', error);
    } finally {
      setLoadingPreviews((prev) => ({ ...prev, [file.id]: false }));
    }
  };

  useEffect(() => {
    files.forEach((file) => {
      if (!previewData[file.id] && !loadingPreviews[file.id]) {
        if (file.type?.includes('pdf') || file.name.toLowerCase().endsWith('.pdf')) {
          generatePdfPreview(file);
        }
      }
    });
  }, [files]);

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  if (files.length === 0) {
    return null;
  }

  return (
    <Box mt={2}>
      <Box display="flex" flexWrap="wrap" gap={2}>
        {files.map((file) => (
          <Card
            key={file.id}
            sx={{
              width: maxPreviewSize,
              height: maxPreviewSize + 80,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4,
              },
              position: 'relative',
              overflow: 'hidden',
            }}
            onClick={() => window.open(file.signedUrl, '_blank')}
          >
            {/* Área de preview */}
            <Box
              sx={{
                height: maxPreviewSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f5f5f5',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {loadingPreviews[file.id] ? (
                <CircularProgress size={40} />
              ) : previewData[file.id] && previewData[file.id] !== 'pdf-preview' ? (
                <img
                  src={previewData[file.id]}
                  alt={file.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  onError={() => {
                    setPreviewData((prev) => {
                      const newData = { ...prev };
                      delete newData[file.id];
                      return newData;
                    });
                  }}
                />
              ) : (
                <Box textAlign="center">{getFileIcon(file.name, file.type)}</Box>
              )}
            </Box>

            {/* Información del archivo */}
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Tooltip title={file.name}>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    display: 'block',
                    fontWeight: 500,
                    lineHeight: 1.2,
                  }}
                >
                  {file.name}
                </Typography>
              </Tooltip>

              {file.size && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatFileSize(file.size)}
                </Typography>
              )}

              {file.lastModified && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {file.lastModified.toLocaleDateString()}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default DocumentsPreview;
